import { MSG_IDS } from "src/consts/msgIds";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { SESSION_KEYS } from "src/consts/session";
import { CircularProgress, Stack } from "@mui/material";
import BackButton from "src/components/Shared/BackButton";
import { ThemeValues } from "src/components/ThemeUsage/theme";
import { onboardingStatus } from "src/api/smartChargingClients";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { ThemeContext } from "src/components/ThemeUsage/ThemeUsage";
import { useCallback, useContext, useEffect, useState } from "react";
import { SMART_CHARGING_ROUTES } from "src/consts/Paths/SmartCharging";
import { EvseSelfOnboardingDetails } from "src/models/smartChargingClientsModels";

const MAX_API_CALL = 30;
const API_CALL_INTERVAL_MS = 10000;
const MAX_API_CALL_RETRY = 5;

const Connecting = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeName = useContext(ThemeContext);

  const [getEvseSelfOnboardingData] =
    useSessionStorage<EvseSelfOnboardingDetails>(
      SESSION_KEYS.EVSE_SELF_ONBOARDING_DETAILS,
    );

  const [getCustomerAccess] = useSessionStorage<string>(
    SESSION_KEYS.MFE_CUSTOMER_ACCESS,
  );

  const [statusData, setStatusData] = useState({
    uuid: "",
    status: "",
    state: "",
  });

  const [apiCallCounter, setApiCallCounter] = useState(1);
  const [, setApiCallErrorCounter] = useState(1);

  const getOnboardingStatus = useCallback(async () => {
    const uuid = getEvseSelfOnboardingData()?.uuid;
    const customerAccess = getCustomerAccess();
    if (!uuid || !customerAccess) return;

    try {
      const response = await onboardingStatus({
        uuid,
        xIsonCustomerToken: customerAccess,
      });
      setStatusData({
        uuid: response.uuid,
        status: response.status,
        state: response.state,
      });

      if (response && response.state === "ACTIVATED") {
        navigate(SMART_CHARGING_ROUTES.CONNECTING_SUCCESS);
      }

      if (response && response.state === "ERROR") {
        if (response.status === "MISSING_DATA") {
          navigate(
            `${SMART_CHARGING_ROUTES.CONNECTING_ERROR}?msgId=${MSG_IDS.smart_charging_onboarding_missing_data}`,
          );
        }
        if (response.status === "DUPLICATE_CHARGE_POINT_ID") {
          navigate(
            `${SMART_CHARGING_ROUTES.CONNECTING_ERROR}?msgId=${MSG_IDS.smart_charging_onboarding_duplicate_charge_point_id}`,
          );
        }
      }
    } catch (err) {
      const error = err as Error;

      if (
        error.status === 500 ||
        error.status === 501 ||
        error.status === 502 ||
        error.status === 503
      ) {
        setApiCallErrorCounter((prevValue) => {
          if (prevValue === MAX_API_CALL_RETRY) {
            navigate(SMART_CHARGING_ROUTES.CONNECTING_ERROR);
          }
          return prevValue + 1;
        });
      } else {
        navigate(SMART_CHARGING_ROUTES.CONNECTING_ERROR);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getOnboardingStatus();
  }, [getOnboardingStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (apiCallCounter < MAX_API_CALL) {
        setApiCallCounter((prevValue) => prevValue + 1);
        getOnboardingStatus();
      }
      if (apiCallCounter === MAX_API_CALL) {
        if (statusData.status === "WAITING_FOR_CONNECTION") {
          navigate(
            `${SMART_CHARGING_ROUTES.CONNECTING_ERROR}?msgId=${MSG_IDS.smart_charging_onboarding_waiting_for_connection}`,
          );
          return;
        }
        if (statusData.status === "WAITING_FOR_BOOT_NOTIFICATION") {
          navigate(
            `${SMART_CHARGING_ROUTES.CONNECTING_ERROR}?msgId=${MSG_IDS.smart_charging_onboarding_waiting_for_boot_notification}`,
          );
          return;
        }
        navigate(SMART_CHARGING_ROUTES.CONNECTING_ERROR);
      }
    }, API_CALL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCallCounter, getOnboardingStatus, navigate]);

  return (
    <>
      <Stack gap={10}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <BackButton
            variant={
              themeName === ThemeValues.lichtblick ||
              themeName === ThemeValues.SMA
                ? "dark"
                : "light"
            }
            onClick={() => navigate(-1)}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              "&:hover": {
                backgroundColor: theme.palette.background.default,
              },
              display: { xs: "none", sm: "flex" },
            })}
          />
          <Text
            text={t("page.wallboxConnecting.pageTitle")}
            sx={(theme) => ({
              fontWeight: 500,
              fontSize: "32px",
              lineHeight: "40px",
              fontFamily: "officina",
              color: theme.palette.mainFont.primary,
            })}
          />
        </Stack>

        <Text
          text={t("page.wallboxConnecting.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
        <Stack alignItems={"center"}>
          <CircularProgress
            sx={(theme) => ({
              color: theme.palette.mainFont.primary,
            })}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default Connecting;
